// eslint-disable-next-line monorepo-cop/no-relative-import-outside-package
import { version as cinnamonVersion } from '../../../../eig/package.json'
import { version as uiVersion } from '../../../package.json'

import { useGetEstimatorCallbacks, useGetUserSmsConnection } from '@/api'
import { ROUTER_PATHS } from '@/lib/constants'
import { whitelabelConfig } from '@/lib/constants/whitelabelConfig'
import { heap } from '@/lib/utils'
import {
  getUserColors,
  getUserInitials,
  getUserName,
  getUserPicture,
  useAuth0,
  User,
} from '@eigtech/ui-shared-auth'
import { UserProfileImageDrawer } from '@eigtech/ui-shared-auth-web'
import { useGetInboxSummary } from '@eigtech/ui-shared-messaging'
import { Sentry } from '@eigtech/ui-shared-sentry'
import { Avatar, AvatarFallback, AvatarImage } from '@eigtech/ui/components/avatar'
import { Badge } from '@eigtech/ui/components/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@eigtech/ui/components/dropdown-menu'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@eigtech/ui/components/sidebar'
import { toast } from '@eigtech/ui/components/sonner'
import { useDisclosure } from '@eigtech/ui/hooks/use-disclosure'
import { cn } from '@eigtech/ui/lib/utils'
import { Link } from '@tanstack/react-router'
import {
  BookOpenIcon,
  BugIcon,
  CalendarDaysIcon,
  ChevronsUpDown,
  HomeIcon,
  InboxIcon,
  LogOut,
  PhoneIncomingIcon,
  SmartphoneIcon,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { FaAppStore, FaGooglePlay } from 'react-icons/fa'
import { useCopyToClipboard } from 'react-use'
import { EstimatorLicenseDrawer } from '../Core/EstimatorLicense'
import { UserSmsModal } from '../Domains/Sms/UserSmsModal'

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  return (
    <Sidebar collapsible="icon" {...props}>
      <NavHeader />
      <SidebarContent>
        <NavMain />
      </SidebarContent>
      <SidebarFooter>
        <NavFooter />
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}

/**
 * On mobile the sidebar is a sheet layered over the page, so it has to be dismissed explicitly
 * once a nav item takes the user somewhere.
 */
function useCloseMobileSidebar() {
  const { setOpenMobile } = useSidebar()

  return () => setOpenMobile(false)
}

function NavHeader() {
  const { state } = useSidebar()

  return (
    <SidebarHeader className={cn(state === 'collapsed' && 'hidden')}>
      <SidebarMenu>
        <SidebarMenuItem className="text-center text-lg font-bold tracking-wide">
          {whitelabelConfig.title}
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarHeader>
  )
}

function NavMain() {
  const closeMobileSidebar = useCloseMobileSidebar()

  return (
    <SidebarGroup>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            render={<Link to={ROUTER_PATHS.home.route} />}
            tooltip="Home"
            onClick={closeMobileSidebar}
          >
            <HomeIcon />
            <span>Home</span>
          </SidebarMenuButton>
        </SidebarMenuItem>

        <InboxMenuItem />

        <CallbacksMenuItem />

        <SidebarMenuItem>
          <SidebarMenuButton
            render={<Link to={ROUTER_PATHS.schedule.route} />}
            tooltip="Schedule"
            onClick={closeMobileSidebar}
          >
            <CalendarDaysIcon />
            <span>Schedule</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarGroup>
  )
}

function InboxMenuItem() {
  const closeMobileSidebar = useCloseMobileSidebar()
  const { data: inbox } = useGetInboxSummary()

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        className="relative"
        render={<Link to={ROUTER_PATHS.inbox.route} />}
        tooltip={`Inbox${inbox?.unreadMessages ? ` (${inbox.unreadMessages} messages)` : ''}`}
        onClick={closeMobileSidebar}
      >
        <InboxIcon />
        <span>Inbox</span>
        <CountBadge count={inbox?.unreadMessages ?? 0} label="messages" />
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

function CallbacksMenuItem() {
  const closeMobileSidebar = useCloseMobileSidebar()
  const { callbacks } = useGetEstimatorCallbacks()

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        className="relative"
        render={<Link to={ROUTER_PATHS.callbacks.route} />}
        tooltip={`Callbacks${callbacks.length ? ` (${callbacks.length} requests)` : ''}`}
        onClick={closeMobileSidebar}
      >
        <PhoneIncomingIcon />
        <span>Callbacks</span>
        <CountBadge count={callbacks.length} label="requests" />
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

function CountBadge({ count, label }: { count: number; label: string }) {
  const { state } = useSidebar()

  if (!count) return null

  if (state === 'collapsed') {
    return (
      <div className="bg-destructive absolute right-0 top-0 size-2.5 rounded-full">
        <span className="sr-only">
          {count} {label}
        </span>
      </div>
    )
  }

  return (
    <Badge className="absolute right-1" radius="full" variant="destructive">
      {count > 99 ? `${99}+` : count} <span className="sr-only">{label}</span>
    </Badge>
  )
}

function NavFooter() {
  const { isMobile } = useSidebar()
  const closeMobileSidebar = useCloseMobileSidebar()

  return (
    <SidebarGroup className="px-0">
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            render={<Link to={ROUTER_PATHS.documentation.route} />}
            tooltip="Documentation"
            onClick={closeMobileSidebar}
          >
            <BookOpenIcon />
            <span>Documentation</span>
          </SidebarMenuButton>
        </SidebarMenuItem>

        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger render={<SidebarMenuButton tooltip="Mobile Apps" />}>
              <SmartphoneIcon />
              <span>Mobile Apps</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="min-w-56 rounded-lg"
              side={isMobile ? 'bottom' : 'right'}
              sideOffset={4}
            >
              <DropdownMenuGroup>
                <DropdownMenuItem
                  className="text-popover-foreground cursor-pointer hover:no-underline"
                  render={<a href={whitelabelConfig.iosAppLink} rel="noreferrer" target="_blank" />}
                  onClick={closeMobileSidebar}
                >
                  <FaAppStore />
                  <span>iOS App Store</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-popover-foreground cursor-pointer hover:no-underline"
                  render={
                    <a href={whitelabelConfig.androidAppLink} rel="noreferrer" target="_blank" />
                  }
                  onClick={closeMobileSidebar}
                >
                  <FaGooglePlay />
                  <span>Google Play Store</span>
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>

        <SidebarMenuItem>
          <SidebarMenuButton
            render={
              <a
                href="https://eigtechnology.atlassian.net/jira/software/form/cd1a1b01-5e79-4f99-b225-e16e39617bc1?atlOrigin=eyJpIjoiODNiZTc3MmEwNjQ0NDMzZDg4OGQwNDhhZGI3YzQwZmUiLCJwIjoiaiJ9"
                target="jiraForm"
              />
            }
            tooltip="Submit Bug"
            onClick={closeMobileSidebar}
          >
            <BugIcon />
            <span>Submit Bug</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarGroup>
  )
}

function NavUser() {
  const { isMobile } = useSidebar()
  const { user, logout } = useAuth0()

  async function handleLogout() {
    heap.resetIdentity()

    Sentry.setUser(null)

    logout({
      returnTo: window.location.origin,
    })
  }

  const {
    open: isEstimatorLicenseOpen,
    onOpen: onEstimatorLicenseOpen,
    onClose: onEstimatorLicenseClose,
  } = useDisclosure()
  const {
    open: isUserProfileImageOpen,
    onOpen: onUserProfileImageOpen,
    onClose: onUserProfileImageClose,
  } = useDisclosure()
  const [isSmsModalOpen, setIsSmsModalOpen] = useState(false)

  const { data: smsConnection, isPending: isPendingSmsConnection } = useGetUserSmsConnection()

  const [state, copyToClipboard] = useCopyToClipboard()
  const previousValue = useRef(state.value)

  useEffect(() => {
    const previous = previousValue.current
    previousValue.current = state.value

    if (previous === state.value) return

    if (state.value) {
      toast.success('Copied to clipboard!')
    } else if (state.error) {
      toast.error('Failed to copy to clipboard!')
    }
  }, [state])

  if (!user) return null

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <SidebarMenuButton
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  size="lg"
                />
              }
            >
              <UserAvatar user={user} />
              <UserInfo user={user} />
              <ChevronsUpDown className="ml-auto size-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="min-w-56 rounded-lg"
              side={isMobile ? 'bottom' : 'right'}
              sideOffset={4}
            >
              <DropdownMenuGroup>
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <UserAvatar user={user} />
                    <UserInfo user={user} />
                  </div>
                </DropdownMenuLabel>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={onEstimatorLicenseOpen}>
                  Update License Number
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onUserProfileImageOpen}>
                  Update Profile Image
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={isPendingSmsConnection}
                  onClick={() => setIsSmsModalOpen(true)}
                >
                  Add phone number for SMS login (optional)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => copyToClipboard(uiVersion)}>
                  UI v{uiVersion}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => copyToClipboard(cinnamonVersion)}>
                  Cinnamon v{cinnamonVersion}
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      {isEstimatorLicenseOpen && (
        <EstimatorLicenseDrawer isOpen onClose={onEstimatorLicenseClose} />
      )}
      {isUserProfileImageOpen && (
        <UserProfileImageDrawer isOpen onClose={onUserProfileImageClose} />
      )}
      <UserSmsModal
        open={isSmsModalOpen}
        phoneNumber={smsConnection?.phoneNumber}
        onOpenChange={setIsSmsModalOpen}
      />
    </>
  )
}

function UserAvatar({ user }: { user: User }) {
  const { color, fontColor } = getUserColors(user)

  return (
    <Avatar
      className="bg-(--user-bg) h-8 w-8 rounded-full"
      style={
        {
          '--user-bg': color,
          '--user-text': fontColor,
        } as React.CSSProperties
      }
    >
      <AvatarImage alt={getUserName(user)} src={getUserPicture(user)} />
      <AvatarFallback className="bg-(--user-bg) text-(--user-text) rounded-full">
        {getUserInitials(user)}
      </AvatarFallback>
    </Avatar>
  )
}

function UserInfo({ user }: { user: User }) {
  return (
    <div className="grid flex-1 text-left text-sm leading-tight">
      <span className="truncate font-medium">{getUserName(user)}</span>
      {user.email && <span className="truncate text-xs">{user.email}</span>}
    </div>
  )
}
